import { FC, useEffect } from 'react'
import type { PortableTextBlock } from '@portabletext/types'
import styles from './GlobalNewsletterModal.module.scss'
import useI18n from '@/hooks/use-i18n'
import FormResult from '@/components/EmailSignupInput/FormResults/FormResults'
import RichText from '@/components/RichText/RichText'
import { headerSerializer, subtitleSerializer } from '@/sections/NewsletterSignUpSection/serializers'
import { useNewsletterForm } from '@/hooks/useNewsletterForm'
import { NewsletterFormFields } from '@/components/NewsletterFormFields/NewsletterFormFields'
import { useNewsletterLocations } from '@/hooks/useNewsletterLocations'

interface NewsletterFormProps {
  onDismiss: () => void
  successMessage: {
    title: PortableTextBlock[]
    subtitle: PortableTextBlock[]
  }
}

const NewsletterForm: FC<NewsletterFormProps> = ({ onDismiss, successMessage }) => {
  const { i18n } = useI18n()

  const { locations } = useNewsletterLocations()

  const {
    formData,
    errors,
    loading,
    formSubmitSuccess,
    formSubmit,
    handleChange,
    handleDateChange,
    handleSubmit,
    submitContext,
  } = useNewsletterForm(onDismiss)

  // Send GTM event when form is successfully submitted
  useEffect(() => {
    if (formSubmitSuccess) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'newsletter_submission',
        formData: {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          location: formData.location,
          birthday: formData.birthday,
        },
      })
    }
  }, [formSubmitSuccess, formData])

  return (
    <>
      {formSubmitSuccess ? (
        <div
          className={styles.successMessageWrapper}
          data-gtm-visibility="success-message"
        >
          <RichText
            value={successMessage.title}
            components={headerSerializer}
          />
          <RichText
            value={successMessage.subtitle}
            components={subtitleSerializer}
          />
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className={styles.form}
        >
          <NewsletterFormFields
            formData={formData}
            errors={errors}
            loading={loading}
            locations={locations}
            handleChange={handleChange}
            handleDateChange={handleDateChange}
            inputClassName={styles.input}
            selectClassName={styles.select}
            rowClassName={styles.row}
            nameRowClassName={styles.nameRow}
            formFieldClassName={styles.formField}
            submitButtonClassName={styles.submitButton}
            errorClassName={styles.error}
          />

          {formSubmit && !loading && (
            <div className={styles.FormResults}>
              {formSubmitSuccess ? (
                <FormResult
                  status="success"
                  successMessage={
                    submitContext === 'already_subscribed' ? (
                      <p>{i18n('alreadyBaoBaoInsider')}</p>
                    ) : submitContext === 'updated' ? (
                      <p>{i18n('profileUpdated')}</p>
                    ) : submitContext === 'resubscribed' ? (
                      <p>{i18n('welcomeBackResubscribed')}</p>
                    ) : (
                      <>
                        {Array.isArray(successMessage?.title) && successMessage.title.length > 0 ? (
                          <RichText
                            value={successMessage.title}
                            components={headerSerializer}
                          />
                        ) : (
                          <p>{i18n('thankYou')}</p>
                        )}
                        {Array.isArray(successMessage?.subtitle) && successMessage.subtitle.length > 0 && (
                          <RichText
                            value={successMessage.subtitle}
                            components={subtitleSerializer}
                          />
                        )}
                      </>
                    )
                  }
                />
              ) : (
                <FormResult
                  status="error"
                  errorMessage={<p>{i18n('somethingWentWrong')}</p>}
                />
              )}
            </div>
          )}
        </form>
      )}
    </>
  )
}

export default NewsletterForm
